import * as React from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout, SettingsNav } from "@bi-studio/shared/components";
import { useAuth } from "@bi-studio/shared/hooks";
import { Container, Stack, Title, Text, SimpleGrid, Card, Group, ThemeIcon, useMantineColorScheme } from "@mantine/core";
import { ShieldCheck, Lock, ChevronRight } from "lucide-react";
import { AiAssistant } from "@/components/ai";

export default function SettingsPage() {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();
    const { colorScheme } = useMantineColorScheme();

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    React.useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            navigate("/login");
        }
    }, [isAuthenticated, isLoading, navigate]);

    if (!isAuthenticated && !isLoading) {
        return null;
    }

    const sidebarContent = <SettingsNav />;

    const settingsOptions = [
        {
            title: "MFA Settings",
            description: "Manage Multi-Factor Authentication for your account security.",
            icon: ShieldCheck,
            path: "/settings/mfa",
            color: "blue"
        },
        {
            title: "Change Password",
            description: "Update your password periodically to stay secure.",
            icon: Lock,
            path: "/settings/password",
            color: "indigo"
        }
    ];

    return (
        <AppLayout
            showSidebar
            sidebarContent={sidebarContent}
            onLogout={handleLogout}
            title="Settings"
            subtitle="Account & Security"
            floatingContent={<AiAssistant />}
        >
            <Container size="xl" p={0}>
                <Stack gap="xl">
                    <div>
                        <Title order={2} fw={700}>Account Settings</Title>
                        <Text c="dimmed">Manage your profile, security, and preferences.</Text>
                    </div>

                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                        {settingsOptions.map((option) => (
                            <Card 
                                key={option.path} 
                                withBorder 
                                padding="xl" 
                                radius="md" 
                                component="button"
                                style={{ 
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'var(--mantine-color-white)',
                                    borderColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'var(--mantine-color-default-border)'
                                }}
                                className="hover:scale-[1.02] hover:shadow-md"
                                onClick={() => navigate(option.path)}
                            >
                                <Group wrap="nowrap" align="flex-start">
                                    <ThemeIcon size={48} radius="md" variant="light" color={option.color}>
                                        <option.icon size={24} />
                                    </ThemeIcon>
                                    <div style={{ flex: 1 }}>
                                        <Group justify="space-between" wrap="nowrap">
                                            <Text fw={600} size="lg">{option.title}</Text>
                                            <ChevronRight size={18} color="var(--mantine-color-dimmed)" />
                                        </Group>
                                        <Text size="sm" c="dimmed" mt={4}>
                                            {option.description}
                                        </Text>
                                    </div>
                                </Group>
                            </Card>
                        ))}
                    </SimpleGrid>
                </Stack>
            </Container>
        </AppLayout>
    );
}
